import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import SearchableSelect from './components/SearchableSelect';

const SERVICES_LIST = ['applications', 'resume', 'portfolio', 'linkedin', 'github', 'courses', 'badge', 'job-links'];

export default function ScoringQueueDashboard() {
  // Server-side stats from /api/lead-scoring-queue-stats/ (for stat cards)
  const [statsData, setStatsData] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 50;
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Table row state
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Date filter (defaults to today's date in local time)
  const [selectedDate, setSelectedDate] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  });

  // Lists for dropdown options
  const [associates, setAssociates] = useState([]);
  const [jobRoles, setJobRoles] = useState([]);

  // Advanced Filters toggle state
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  // Filter selections states
  const [selectedAssociate, setSelectedAssociate] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedWorkPref, setSelectedWorkPref] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedServices, setSelectedServices] = useState([]);
  const [servicesOptedLogic, setServicesOptedLogic] = useState('and');

  // Search filter
  const [search, setSearch] = useState('');
  const [activeCardFilter, setActiveCardFilter] = useState('');

  const hasActiveDropdownFilters = Boolean(
    search ||
    selectedServices.length > 0 ||
    selectedAssociate ||
    selectedRole ||
    selectedWorkPref ||
    selectedStatus
  );

  const hasActiveFilters = hasActiveDropdownFilters || Boolean(activeCardFilter);

  // Sorting state
  const [sortConfig, setSortConfig] = useState({ key: 'scored_job_count', direction: 'desc' });

  // Helper to change date by offset days
  const handleDateChange = (offset) => {
    const [y, m, d] = selectedDate.split('-').map(Number);
    const dt = new Date(y, m - 1, d);
    dt.setDate(dt.getDate() + offset);
    setSelectedDate(`${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`);
  };

  const resetToToday = () => {
    const d = new Date();
    setSelectedDate(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
  };

  // Fetch aggregate stats for cards (date / services only — not on page or sort change)
  useEffect(() => {
    const controller = new AbortController();
    const servicesParam = selectedServices.length > 0
      ? `&services_opted=${encodeURIComponent(selectedServices.join(','))}&services_opted_logic=${encodeURIComponent(servicesOptedLogic)}`
      : '';

    const fetchStats = async () => {
      setStatsLoading(true);
      try {
        const res = await axios.get(
          `/api/lead-scoring-queue-stats/?date=${selectedDate}${servicesParam}`,
          { signal: controller.signal }
        );
        setStatsData(res.data);
      } catch (err) {
        if (axios.isCancel(err)) return;
        console.error('Failed to fetch scoring queue stats', err);
      } finally {
        if (!controller.signal.aborted) setStatsLoading(false);
      }
    };

    fetchStats();
    return () => { controller.abort(); };
  }, [selectedDate, selectedServices, servicesOptedLogic]);

  // Fetch paginated table rows (refetch on page, card filter, or sort)
  useEffect(() => {
    const controller = new AbortController();
    const servicesParam = selectedServices.length > 0
      ? `&services_opted=${encodeURIComponent(selectedServices.join(','))}&services_opted_logic=${encodeURIComponent(servicesOptedLogic)}`
      : '';
    const cardParam = activeCardFilter
      ? `&card_filter=${encodeURIComponent(activeCardFilter)}`
      : '';
    const sortBy = sortConfig?.key || 'scored_job_count';
    const sortDir = sortConfig?.direction || 'desc';
    const sortParam = `&sort_by=${encodeURIComponent(sortBy)}&sort_dir=${encodeURIComponent(sortDir)}`;

    const fetchRows = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get(
          `/api/lead-scoring-queue-dashboard/?date=${selectedDate}&page=${currentPage}&page_size=${PAGE_SIZE}${servicesParam}${cardParam}${sortParam}`,
          { signal: controller.signal }
        );
        const rawResults = res.data.results || [];
        const sanitized = rawResults.map(row => {
          let parsedServices = [];
          if (typeof row.services_opted === 'string') {
            try { parsedServices = JSON.parse(row.services_opted); } catch (e) { parsedServices = []; }
          } else if (Array.isArray(row.services_opted)) {
            parsedServices = row.services_opted;
          }
          return { ...row, services_opted: parsedServices };
        });
        setResults(sanitized);
        setTotalCount(res.data.count || 0);
        setTotalPages(res.data.total_pages || 1);
      } catch (err) {
        if (axios.isCancel(err)) return;
        console.error('Failed to fetch lead scoring queue data', err);
        setError('Failed to load lead scoring queue dashboard data. Please try again.');
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    fetchRows();
    return () => { controller.abort(); };
  }, [selectedDate, selectedServices, servicesOptedLogic, currentPage, activeCardFilter, sortConfig]);

  // Reset to page 1 when date or services filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedDate, selectedServices, servicesOptedLogic]);


  // Fetch filter dropdown options on mount
  useEffect(() => {
    const controller = new AbortController();
    // 1. Fetch associates
    axios.get('/api/active-ops-users/', { signal: controller.signal })
      .then(res => {
        setAssociates(res.data?.opsUsers || []);
      })
      .catch(err => {
        if (!axios.isCancel(err)) {
          console.error('Failed to fetch associates', err);
        }
      });

    // 2. Fetch job roles
    axios.get('/api/v1/jobroles/?page_size=500', { signal: controller.signal })
      .then(res => {
        const data = Array.isArray(res.data) ? res.data : (res.data.results || []);
        setJobRoles(data);
      })
      .catch(err => {
        if (!axios.isCancel(err)) {
          console.error('Failed to fetch job roles', err);
        }
      });

    return () => {
      controller.abort();
    };
  }, []);

  // Filter results by search term, associate, role, preference except status
  const resultsFilteredExceptStatus = useMemo(() => {
    return results.filter(row => {
      // 1. Search term filter
      if (search.trim()) {
        const query = search.toLowerCase();
        const name = (row.lead_name || '').toLowerCase();
        const role = (row.job_role_name || '').toLowerCase();
        const ops = (row.ops_name || '').toLowerCase();
        if (!name.includes(query) && !role.includes(query) && !ops.includes(query)) return false;
      }
      // 2. Associate filter
      if (selectedAssociate) {
        if (selectedAssociate === 'unassigned') {
          if (row.ops_id) return false;
        } else {
          if (String(row.ops_id) !== String(selectedAssociate)) return false;
        }
      }
      // 3. Job role filter
      if (selectedRole) {
        if (String(row.job_role_id) !== String(selectedRole)) return false;
      }
      // 4. Work preference filter
      if (selectedWorkPref) {
        if ((row.work_preference || '').toLowerCase() !== selectedWorkPref.toLowerCase()) return false;
      }
      return true;
    });
  }, [results, search, selectedAssociate, selectedRole, selectedWorkPref]);

  // Compute statusCounts dynamically from resultsFilteredExceptStatus
  const statusCounts = useMemo(() => {
    let active = 0;
    let inProgress = 0;
    resultsFilteredExceptStatus.forEach(row => {
      if (row.status === 'ACTIVE') active++;
      else if (row.status === 'In Progress') inProgress++;
    });
    return {
      'ACTIVE': active,
      'In Progress': inProgress
    };
  }, [resultsFilteredExceptStatus]);

  // Filter results finally by status (card filter + sort are server-side)
  const filteredResults = useMemo(() => {
    let temp = resultsFilteredExceptStatus;
    if (selectedStatus) {
      temp = temp.filter(row => row.status === selectedStatus);
    }
    return temp;
  }, [resultsFilteredExceptStatus, selectedStatus]);

  const requestSort = (key) => {
    setCurrentPage(1);
    if (sortConfig && sortConfig.key === key) {
      if (sortConfig.direction === 'asc') {
        setSortConfig({ key, direction: 'desc' });
      } else {
        setSortConfig({ key: 'scored_job_count', direction: 'desc' });
      }
    } else {
      setSortConfig({ key, direction: 'asc' });
    }
  };

  const toggleCardFilter = (key) => {
    setCurrentPage(1);
    setActiveCardFilter(prev => (prev === key ? '' : key));
  };

  const getSortIcon = (key) => {
    if (!sortConfig || sortConfig.key !== key) return '↕';
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  const emptyStateCopy = useMemo(() => {
    const byCard = {
      scored: {
        title: 'No scored leads found',
        body: 'No scored leads match your filters.',
      },
      not_scored: {
        title: 'No pending leads found',
        body: 'No pending (not scored) leads match your filters.',
      },
      jobs: {
        title: 'No leads with uploaded jobs',
        body: 'No leads with uploaded jobs match your filters.',
      },
      external_jobs: {
        title: 'No leads with external jobs',
        body: 'No leads with external jobs match your filters.',
      },
      scored_jobs: {
        title: 'No leads with scored jobs',
        body: 'No leads with scored jobs match your filters.',
      },
      tasks: {
        title: 'No leads with created tasks',
        body: 'No leads with created tasks match your filters.',
      },
      task_gen: {
        title: 'No leads pending task generation',
        body: 'No leads pending task generation match your filters.',
      },
    };
    if (activeCardFilter && byCard[activeCardFilter]) {
      return byCard[activeCardFilter];
    }
    return {
      title: 'All scoring completed!',
      body: "No active leads currently have pending scoring for today's external jobs matching your filters.",
    };
  }, [activeCardFilter]);

  const formatScore = (val) => {
    if (val === null || val === undefined) return '—';
    return Number(val).toFixed(0);
  };

  const formattedDateString = useMemo(() => {
    if (!selectedDate) return '';
    const [y, m, d] = selectedDate.split('-').map(Number);
    const dt = new Date(y, m - 1, d);
    return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }, [selectedDate]);

  const getBreakdownTooltip = (row) => {
    const parts = [];
    if (row.tasks_pending > 0) parts.push(`Pending: ${row.tasks_pending}`);
    if (row.tasks_in_progress > 0) parts.push(`In Progress: ${row.tasks_in_progress}`);
    if (row.tasks_completed > 0) parts.push(`Completed: ${row.tasks_completed}`);
    if (row.tasks_awaiting_email > 0) parts.push(`Awaiting Email: ${row.tasks_awaiting_email}`);
    if (row.tasks_already_applied > 0) parts.push(`Already Applied: ${row.tasks_already_applied}`);
    if (row.tasks_not_relevant > 0) parts.push(`Not Relevant: ${row.tasks_not_relevant}`);
    if (row.tasks_job_not_found > 0) parts.push(`Job Not Found: ${row.tasks_job_not_found}`);
    if (row.tasks_need_otp > 0) parts.push(`Need OTP: ${row.tasks_need_otp}`);
    return parts.join(' | ');
  };

  // Returns a stat card value from statsData (server) with skeleton while loading
  const getStatValue = (key) => {
    if (statsLoading) {
      return <span className="inline-block w-16 h-8 bg-slate-200/60 rounded animate-pulse mt-1" />;
    }
    return statsData ? statsData[key] ?? '—' : '—';
  };

  return (
    <div className="w-full max-w-7xl mx-auto p-4 md:p-8 animate-fadeIn">
      {/* Header and Subtitle */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between border-b border-gray-100 pb-6 mb-6">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 md:text-4xl">
            Lead Scoring Queue
          </h1>
          <p className="text-sm font-medium text-slate-500 mt-2">
            Monitor real-time job uploads, automatic matched scoring, and task completion for active leads.
          </p>
        </div>

        {/* Date Controls */}
        <div className="mt-4 md:mt-0 flex flex-wrap items-center gap-2">
          <button
            onClick={() => handleDateChange(-1)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-slate-600 bg-white hover:bg-slate-50 transition-colors shadow-sm text-sm font-semibold cursor-pointer active:scale-95"
            title="Previous Day"
          >
            ← Prev
          </button>

          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-slate-700 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm font-medium shadow-inner"
          />

          <button
            onClick={() => handleDateChange(1)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-slate-600 bg-white hover:bg-slate-50 transition-colors shadow-sm text-sm font-semibold cursor-pointer active:scale-95"
            title="Next Day"
          >
            Next →
          </button>

          <button
            onClick={resetToToday}
            className="px-3 py-2 border border-indigo-200 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-lg text-sm font-semibold transition-colors shadow-sm cursor-pointer active:scale-95"
          >
            Today
          </button>
        </div>
      </div>

      {/* Sub-navigation Tabs */}
      <div className="flex items-center gap-2 mb-8 bg-slate-100/60 p-1.5 rounded-xl border border-slate-200/50 w-fit select-none">
        <Link
          to="/scoring-queue-dashboard"
          className="px-4 py-2 bg-white text-indigo-700 shadow-sm border border-slate-200/40 rounded-lg text-sm font-bold flex items-center gap-1.5 transition-all"
        >
          <i className="fas fa-list-ul text-xs" />
          Summary Dashboard
        </Link>
        <Link
          to="/trends-dashboard"
          className="px-4 py-2 text-slate-600 hover:text-slate-800 rounded-lg text-sm font-semibold flex items-center gap-1.5 transition-all hover:bg-slate-200/40"
        >
          <i className="fas fa-chart-line text-xs" />
          Trends Dashboard
        </Link>
      </div>


      {error && (
        <div className="bg-rose-50 border-l-4 border-rose-500 p-4 rounded-r-lg mb-8 shadow-sm">
          <div className="flex">
            <span className="text-rose-700 font-medium">{error}</span>
          </div>
        </div>
      )}

      {/* Metrics Cards Grid — powered by /api/lead-scoring-queue-stats/ */}
      <div className="grid gap-4 grid-cols-2 md:grid-cols-3 lg:grid-cols-4 mb-8">
        <StatCard
          label="Total Leads Scored"
          value={getStatValue('total_leads_scored')}
          desc={`Leads with > 0 scored jobs on ${formattedDateString}`}
          icon={<i className="fas fa-check-circle text-emerald-500 text-xl" />}
          bgColor="bg-emerald-50 border-emerald-200"
          valueColor="text-emerald-700"
          isActive={activeCardFilter === 'scored'}
          onClick={() => toggleCardFilter('scored')}
        />
        <StatCard
          label="Pending Leads (Not Scored)"
          value={getStatValue('pending_not_scored')}
          desc={`Leads without scored jobs on ${formattedDateString}`}
          icon={<i className="fas fa-hourglass-half text-amber-500 text-xl" />}
          bgColor="bg-amber-50 border-amber-200"
          valueColor="text-amber-700"
          isActive={activeCardFilter === 'not_scored'}
          onClick={() => toggleCardFilter('not_scored')}
        />
        <StatCard
          label="Total Jobs Uploaded"
          value={getStatValue('total_jobs')}
          desc={`Jobs posted on ${formattedDateString}`}
          icon={<i className="fas fa-briefcase text-blue-500 text-xl" />}
          bgColor="bg-blue-50 border-blue-200"
          valueColor="text-blue-700"
          isActive={activeCardFilter === 'jobs'}
          onClick={() => toggleCardFilter('jobs')}
        />
        <StatCard
          label="External Jobs"
          value={getStatValue('total_external_jobs')}
          desc={`External site uploads on ${formattedDateString}`}
          icon={<i className="fas fa-external-link-alt text-indigo-500 text-xl" />}
          bgColor="bg-indigo-50 border-indigo-200"
          valueColor="text-indigo-700"
          isActive={activeCardFilter === 'external_jobs'}
          onClick={() => toggleCardFilter('external_jobs')}
        />
        <StatCard
          label="Jobs Scored"
          value={getStatValue('total_scored_jobs')}
          desc={`Scoring models processed on ${formattedDateString}`}
          icon={<i className="fas fa-cog text-slate-500 text-xl" />}
          bgColor="bg-slate-50 border-slate-200"
          valueColor="text-slate-700"
          isActive={activeCardFilter === 'scored_jobs'}
          onClick={() => toggleCardFilter('scored_jobs')}
        />
        <StatCard
          label="Tasks Created"
          value={getStatValue('total_tasks')}
          desc={`Tasks created on ${formattedDateString}`}
          icon={<i className="fas fa-tasks text-purple-500 text-xl" />}
          bgColor="bg-purple-50 border-purple-200"
          valueColor="text-purple-700"
          isActive={activeCardFilter === 'tasks'}
          onClick={() => toggleCardFilter('tasks')}
        />
      </div>

      {/* Filters Section */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 mb-8">
        {/* Status Filter Buttons */}
        <div className="flex flex-wrap items-center gap-2 mb-4 pb-4 border-b border-slate-100">
          <span className="text-xs font-extrabold text-slate-500 uppercase tracking-wider mr-2">Lead Status:</span>
          <button
            type="button"
            onClick={() => setSelectedStatus('')}
            className={`text-xs px-3 py-1.5 rounded-full font-bold transition-all cursor-pointer active:scale-95 border ${!selectedStatus
                ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm'
                : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100'
              }`}
          >
            Total: {totalCount}
          </button>
          <button
            type="button"
            onClick={() => setSelectedStatus('In Progress')}
            className={`text-xs px-3 py-1.5 rounded-full font-bold transition-all cursor-pointer active:scale-95 border ${selectedStatus === 'In Progress'
                ? 'bg-sky-600 text-white border-sky-600 shadow-sm'
                : 'bg-sky-50 text-sky-700 border-sky-100 hover:bg-sky-100'
              }`}
          >
            In Progress: {statusCounts['In Progress']}
          </button>
        </div>

        <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
          {/* Quick Search */}
          <div className="w-full md:w-96 relative">
            <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
              <i className="fas fa-search" />
            </span>
            <input
              type="text"
              placeholder="Search by lead, job role, or ops agent..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 focus:bg-white rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm font-medium transition-all"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600"
              >
                <i className="fas fa-times-circle" />
              </button>
            )}
          </div>

          {/* Toggle Advanced filters */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              className={`px-4 py-2 border rounded-xl text-sm font-semibold transition-all flex items-center gap-2 cursor-pointer active:scale-95 ${showAdvancedFilters || selectedAssociate || selectedRole || selectedWorkPref || selectedServices.length > 0
                  ? 'bg-indigo-50 border-indigo-200 text-indigo-700 shadow-sm'
                  : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                }`}
            >
              <i className="fas fa-filter text-xs" />
              Advanced Filters
              <span className="ml-1 text-xs">{showAdvancedFilters ? '▲' : '▼'}</span>
            </button>
          </div>
        </div>

        {/* Collapsed Advanced Filters dropdown */}
        {showAdvancedFilters && (
          <div className="mt-4 pt-4 border-t border-slate-100 animate-slideDown">
            {/* Top Row: Dropdown Selects */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              {/* Associate Filter */}
              <div>
                <label className="block text-xs font-extrabold text-slate-500 uppercase tracking-wider mb-2">Assigned Ops Agent</label>
                <SearchableSelect
                  options={[
                    { value: 'unassigned', label: 'Unassigned / NA' },
                    ...associates.map((assoc) => ({
                      value: String(assoc.id),
                      label: `${assoc.name} (${assoc.email})`
                    }))
                  ]}
                  value={selectedAssociate}
                  onChange={setSelectedAssociate}
                  placeholder="All Agents"
                />
              </div>

              {/* Job Role Searchable Select */}
              <div>
                <label className="block text-xs font-extrabold text-slate-500 uppercase tracking-wider mb-2">Target Job Role</label>
                <SearchableSelect
                  options={jobRoles.map(role => ({ value: String(role.id), label: role.name }))}
                  value={selectedRole}
                  onChange={setSelectedRole}
                  placeholder="Select job role..."
                />
              </div>

              {/* Work Preference Filter */}
              <div>
                <label className="block text-xs font-extrabold text-slate-500 uppercase tracking-wider mb-2">Work Preference</label>
                <select
                  value={selectedWorkPref}
                  onChange={(e) => setSelectedWorkPref(e.target.value)}
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 font-medium transition-all"
                >
                  <option value="">All Preferences</option>
                  {['Remote', 'Hybrid', 'On-site'].map(pref => (
                    <option key={pref} value={pref}>{pref}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Bottom Row: Services Opted */}
            <div className="border-t border-slate-100 pt-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-extrabold text-slate-500 uppercase tracking-wider block">Filter by Services Opted</span>
                    <div className="relative group flex items-center">
                      <span className="text-gray-400 hover:text-gray-600 cursor-help text-xs bg-gray-100 border border-gray-200 rounded-full w-5 h-5 flex items-center justify-center font-bold font-serif">i</span>
                      <div className="absolute left-6 bottom-0 hidden group-hover:block bg-gray-900 text-white text-xs rounded p-3 w-64 shadow-xl z-50 leading-relaxed font-sans normal-case">
                        <p className="font-semibold mb-1">Filter Logic Options:</p>
                        <p className="mb-2"><strong>Match Any (OR):</strong> Leads having *at least one* of the checked services.</p>
                        <p><strong>Match All (AND):</strong> Leads having *all* of the checked services together.</p>
                      </div>
                    </div>
                  </div>
                  <span className="text-xs text-slate-400 mt-1 block">Limit list to leads that opted for specific packages.</span>
                </div>
                <div className="flex items-center">
                  <span className="text-xs font-semibold text-slate-500 mr-2 uppercase">Matching:</span>
                  <div className="inline-flex rounded-lg shadow-sm">
                    <button
                      onClick={() => setServicesOptedLogic('or')}
                      className={`px-3 py-1 text-xs font-semibold rounded-l-lg border cursor-pointer ${servicesOptedLogic === 'or'
                          ? 'bg-indigo-600 text-white border-indigo-600'
                          : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                        }`}
                    >
                      Match Any (OR)
                    </button>
                    <button
                      onClick={() => setServicesOptedLogic('and')}
                      className={`px-3 py-1 text-xs font-semibold rounded-r-lg border-t border-b border-r cursor-pointer ${servicesOptedLogic === 'and'
                          ? 'bg-indigo-600 text-white border-indigo-600'
                          : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                        }`}
                    >
                      Match All (AND)
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                {SERVICES_LIST.map(service => {
                  const isSelected = selectedServices.includes(service);
                  return (
                    <button
                      key={service}
                      onClick={() => {
                        if (isSelected) {
                          setSelectedServices(selectedServices.filter(s => s !== service));
                        } else {
                          setSelectedServices([...selectedServices, service]);
                        }
                      }}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all cursor-pointer ${isSelected
                          ? 'bg-indigo-50 border-indigo-300 text-indigo-700 shadow-sm'
                          : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'
                        }`}
                    >
                      {service.replace('-', ' ').toUpperCase()}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Actions Row */}
        <div className="mt-4 border-t border-slate-100 pt-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            {hasActiveFilters && (
              <p className="text-sm text-slate-500 font-medium">
                {activeCardFilter ? (
                  <>Card filter active · <strong>{totalCount}</strong> matching lead{totalCount === 1 ? '' : 's'} across all pages.</>
                ) : (
                  <>Selected filters match <strong>{filteredResults.length}</strong> out of <strong>{results.length}</strong> leads on this page.</>
                )}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={() => {
              setSearch('');
              setSelectedServices([]);
              setSelectedAssociate('');
              setSelectedRole('');
              setSelectedWorkPref('');
              setSelectedStatus('');
              setActiveCardFilter('');
              setCurrentPage(1);
            }}
            className="px-4 py-2 border border-slate-200 rounded-xl text-sm font-semibold text-slate-600 bg-white hover:bg-slate-50 transition-colors shadow-sm cursor-pointer active:scale-95"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Row Color Legend / Explanation Note */}
      {/* <div className="bg-white border border-slate-200 rounded-2xl p-4 mb-6 flex flex-col md:flex-row gap-4 md:items-center text-xs text-slate-600 shadow-sm animate-fadeIn">
        <div className="font-extrabold text-slate-500 uppercase tracking-wider flex items-center gap-1">
          <i className="fas fa-info-circle text-indigo-500 text-sm" />
          Queue Row Legend:
        </div>
        <div className="flex flex-wrap gap-4">
          <div className="flex items-center gap-2">
            <span className="inline-block w-4 h-4 rounded bg-emerald-50/50 border border-emerald-200 border-l-4 border-l-emerald-500"></span>
            <span><strong>ACTIVE Leads</strong>: Leads whose subscription package is currently active. Priority processing.</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-block w-4 h-4 rounded bg-sky-50/50 border border-sky-200 border-l-4 border-l-sky-400"></span>
            <span><strong>In Progress Leads</strong>: Leads whose profile/onboarding is still being configured or updated.</span>
          </div>
        </div>
      </div> */}

      {/* Main Data Table */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-slate-700 text-xs font-bold uppercase tracking-wider select-none">
                <th className="py-4 px-6 font-bold text-slate-700 w-12 text-center select-none">S.No</th>
                <th className="py-4 px-6 font-bold cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => requestSort('lead_name')}>
                  Lead Details <span className="text-slate-400 ml-1">{getSortIcon('lead_name')}</span>
                </th>
                <th className="py-4 px-6 font-bold cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => requestSort('job_role_name')}>
                  Target Job Role <span className="text-slate-400 ml-1">{getSortIcon('job_role_name')}</span>
                </th>
                <th className="py-4 px-6 font-bold cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => requestSort('ops_name')}>
                  Assigned Ops Agent <span className="text-slate-400 ml-1">{getSortIcon('ops_name')}</span>
                </th>
                <th className="py-4 px-6 font-bold cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => requestSort('job_count')}>
                  Scrapped Jobs <span className="text-slate-400 ml-1">{getSortIcon('job_count')}</span>
                </th>
                <th className="py-4 px-6 font-bold cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => requestSort('scored_job_count')}>
                  Scored Jobs <span className="text-slate-400 ml-1">{getSortIcon('scored_job_count')}</span>
                </th>
                <th className="py-4 px-6 font-bold cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => requestSort('task_count')}>
                  Tasks Created <span className="text-slate-400 ml-1">{getSortIcon('task_count')}</span>
                </th>
                <th className="py-4 px-6 font-bold text-center">
                  Score range (Min/Max)
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-sm">
              {loading ? (
                <tr>
                  <td colSpan={8} className="py-12 text-center text-slate-400 font-medium">
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-6 h-6 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
                      Fetching latest scoring updates...
                    </div>
                  </td>
                </tr>
              ) : filteredResults.length > 0 ? (
                filteredResults.map((row, index) => (
                  <tr
                    key={row.lead_id}
                    className={`transition-all group border-b border-slate-100 ${row.status === 'ACTIVE'
                        ? 'bg-emerald-50/15 hover:bg-emerald-50/30'
                        : row.status === 'In Progress'
                          ? 'bg-sky-50/15 hover:bg-sky-50/30'
                          : 'hover:bg-slate-50/70'
                      }`}
                  >
                    <td className={`py-4 px-6 text-center text-slate-500 font-semibold w-12 ${row.status === 'ACTIVE'
                        ? 'border-l-4 border-l-emerald-500/70'
                        : row.status === 'In Progress'
                          ? 'border-l-4 border-l-sky-400/70'
                          : ''
                      }`}>
                      {index + 1}
                    </td>
                    <td className="py-4 px-6">
                      <Link
                        to={`/leads/${row.lead_id}`}
                        className="font-semibold text-slate-800 hover:text-indigo-600 transition-colors block text-base"
                      >
                        {row.lead_name}
                      </Link>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs font-semibold text-slate-400 uppercase tracking-tight">ID: {row.lead_id}</span>
                        {(Array.isArray(row.services_opted)
                          ? row.services_opted.includes('job-links')
                          : typeof row.services_opted === 'string' && row.services_opted.includes('job-links')
                        ) && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-blue-50 text-blue-700 border border-blue-100 uppercase tracking-wider" title="Opts for Job Board (Job Links)">
                            Job Board
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-4 px-6 font-medium text-slate-700">{row.job_role_name || '—'}</td>
                    <td className="py-4 px-6">
                      {row.ops_name ? (
                        <div className="flex items-center gap-2">
                          <span className="w-6 h-6 bg-indigo-100 text-indigo-700 rounded-full flex items-center justify-center font-bold text-xs uppercase">
                            {row.ops_name.slice(0, 2)}
                          </span>
                          <span className="font-medium text-slate-700">{row.ops_name}</span>
                        </div>
                      ) : (
                        <span className="text-slate-400 italic">Unassigned</span>
                      )}
                    </td>
                    <td className="py-4 px-6">
                      <div className="font-semibold text-slate-800">{row.job_count} jobs</div>
                      <div className="text-xs text-slate-400">ext: {row.external_jobs}</div>
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-slate-800">{row.scored_job_count}</span>
                        {row.external_jobs > 0 && (
                          <div className="w-20 bg-slate-100 rounded-full h-1.5 overflow-hidden">
                            <div
                              className="bg-indigo-500 h-1.5 rounded-full"
                              style={{ width: `${Math.min(100, (row.scored_job_count / row.external_jobs) * 100)}%` }}
                            ></div>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex flex-col items-start gap-1" title={getBreakdownTooltip(row)}>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold ${row.task_count > 0 ? 'bg-indigo-50 text-indigo-700 border border-indigo-100' : 'bg-slate-100 text-slate-500'
                          }`}>
                          {row.task_count} tasks
                        </span>
                        {row.task_count > 0 && (
                          <div className="w-24 bg-slate-200 rounded-full h-2 overflow-hidden flex mt-1">
                            {row.tasks_pending > 0 && (
                              <div
                                className="bg-amber-500 h-2 animate-pulse"
                                style={{ width: `${(row.tasks_pending / row.task_count) * 100}%` }}
                                title={`Pending: ${row.tasks_pending}`}
                              />
                            )}
                            {row.tasks_in_progress > 0 && (
                              <div
                                className="bg-blue-500 h-2"
                                style={{ width: `${(row.tasks_in_progress / row.task_count) * 100}%` }}
                                title={`In Progress: ${row.tasks_in_progress}`}
                              />
                            )}
                            {row.tasks_completed > 0 && (
                              <div
                                className="bg-emerald-500 h-2"
                                style={{ width: `${(row.tasks_completed / row.task_count) * 100}%` }}
                                title={`Completed: ${row.tasks_completed}`}
                              />
                            )}
                            {row.tasks_awaiting_email > 0 && (
                              <div
                                className="bg-purple-500 h-2"
                                style={{ width: `${(row.tasks_awaiting_email / row.task_count) * 100}%` }}
                                title={`Awaiting Email: ${row.tasks_awaiting_email}`}
                              />
                            )}
                            {row.tasks_already_applied > 0 && (
                              <div
                                className="bg-teal-500 h-2"
                                style={{ width: `${(row.tasks_already_applied / row.task_count) * 100}%` }}
                                title={`Already Applied: ${row.tasks_already_applied}`}
                              />
                            )}
                            {row.tasks_not_relevant > 0 && (
                              <div
                                className="bg-rose-500 h-2"
                                style={{ width: `${(row.tasks_not_relevant / row.task_count) * 100}%` }}
                                title={`Not Relevant: ${row.tasks_not_relevant}`}
                              />
                            )}
                            {row.tasks_job_not_found > 0 && (
                              <div
                                className="bg-orange-500 h-2"
                                style={{ width: `${(row.tasks_job_not_found / row.task_count) * 100}%` }}
                                title={`Job Not Found: ${row.tasks_job_not_found}`}
                              />
                            )}
                            {row.tasks_need_otp > 0 && (
                              <div
                                className="bg-pink-500 h-2"
                                style={{ width: `${(row.tasks_need_otp / row.task_count) * 100}%` }}
                                title={`Need OTP: ${row.tasks_need_otp}`}
                              />
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="py-4 px-6 text-center">
                      {row.min_score !== null && row.max_score !== null ? (
                        <div className="inline-flex items-center gap-1.5 bg-slate-50 border border-slate-100 px-3 py-1 rounded-lg">
                          <span className="text-slate-600 font-bold text-xs">{formatScore(row.min_score)}</span>
                          <span className="text-slate-400 font-bold">~</span>
                          <span className="text-slate-800 font-bold text-sm">{formatScore(row.max_score)}</span>
                        </div>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={8} className="py-16 text-center text-slate-400 font-medium">
                    <div className="flex flex-col items-center justify-center gap-3">
                      <div className="w-16 h-16 rounded-full bg-slate-50 flex items-center justify-center text-slate-300 text-2xl">
                        <i className={`fas ${activeCardFilter ? 'fa-filter' : 'fa-check-double'} text-slate-400`} />
                      </div>
                      <span className="text-slate-700 font-bold text-lg">{emptyStateCopy.title}</span>
                      <p className="text-xs text-slate-400 max-w-sm">
                        {emptyStateCopy.body}
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-4 border-t border-slate-100 bg-slate-50/50 rounded-b-2xl">
              <span className="text-sm text-slate-500 font-medium">
                Page <span className="font-bold text-slate-700">{currentPage}</span> of{' '}
                <span className="font-bold text-slate-700">{totalPages}</span>
                {' '}· {totalCount} total leads
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage <= 1 || loading}
                  className="px-4 py-2 text-sm font-semibold border border-slate-200 rounded-lg bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer active:scale-95"
                >
                  ← Prev
                </button>
                {/* Quick page jump buttons (show up to 5) */}
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const pageNum = Math.max(1, Math.min(currentPage - 2, totalPages - 4)) + i;
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      disabled={loading}
                      className={`w-9 h-9 text-sm font-bold rounded-lg border transition-colors cursor-pointer active:scale-95 ${
                        pageNum === currentPage
                          ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm'
                          : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage >= totalPages || loading}
                  className="px-4 py-2 text-sm font-semibold border border-slate-200 rounded-lg bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer active:scale-95"
                >
                  Next →
                </button>
              </div>
            </div>
          )}
      </div>
    </div>
  );
}

// Sub-component for individual statistics cards
function StatCard({ label, value, desc, icon, bgColor, valueColor, isActive, onClick }) {
  return (
    <div
      onClick={onClick}
      className={`p-5 rounded-2xl border shadow-sm flex items-start gap-4 transition-all hover:shadow-md cursor-pointer select-none ${isActive ? 'ring-2 ring-indigo-600 border-transparent bg-indigo-50/20 scale-[1.02]' : bgColor}`}
    >
      <div className="p-3 bg-white rounded-xl shadow-inner border border-slate-100 flex items-center justify-center">
        {icon}
      </div>
      <div>
        <span className="text-xs font-bold uppercase tracking-wider text-slate-400 block">{label}</span>
        <span className={`text-2xl font-black block mt-1 ${valueColor}`}>{value}</span>
        <span className="text-[10px] font-medium text-slate-500 mt-1 block">{desc}</span>
      </div>
    </div>
  );
}
